import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

function getBackendUrl() {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname.startsWith('192.168') || hostname.startsWith('10.')
    ? `http://${hostname}:8000`
    : `${window.location.protocol}//${hostname}`;
}

export default function ScanCapture() {
  const { token } = useParams();
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [pages, setPages] = useState(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const backendUrl = getBackendUrl();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    setImage(file);
    setPreview(URL.createObjectURL(file));
    setError("");
  };

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleUpload = async () => {
    if (!image) return;
    setUploading(true);
    setError("");
    try {
      const data = new FormData();
      data.append("image", image);
      const res = await axios.post(`${backendUrl}/api/v1/scan/${token}/upload/`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPages(res.data.pages);
      setImage(null);
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("This scan session has expired or already been used.");
      } else {
        setError("Failed to upload. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleFinish = async () => {
    setFinishing(true);
    setError("");
    try {
      await axios.post(`${backendUrl}/api/v1/scan/${token}/finish/`);
      setDone(true);
    } catch (err) {
      setError("Failed to process. Please try again.");
    } finally {
      setFinishing(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="type-section-title mb-2">Recipe Scanned!</h2>
          <p className="type-muted">
            {pages} page{pages !== 1 ? "s" : ""} processed. Check your desktop — the recipe form should be filling in now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full">
        <h2 className="type-section-title mb-1 text-center">Scan Recipe</h2>
        <p className="type-muted text-center mb-2">
          Take photos of each page of the recipe
        </p>

        {pages > 0 && (
          <div className="bg-blue-50 text-blue-700 text-sm font-medium text-center py-2 rounded-lg mb-4">
            {pages} page{pages !== 1 ? "s" : ""} scanned
          </div>
        )}

        {preview ? (
          <div className="mb-4">
            <img src={preview} alt="Preview" className="w-full rounded-lg object-cover max-h-72" />
            <button
              onClick={() => { setImage(null); if (preview) URL.revokeObjectURL(preview); setPreview(null); }}
              className="mt-2 text-sm text-gray-500 hover:text-red-500 transition w-full text-center"
            >
              Retake
            </button>
          </div>
        ) : (
          <label className="block mb-4">
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 transition">
              <svg className="w-10 h-10 text-gray-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm text-gray-400">
                {pages > 0 ? "Tap to add another page" : "Tap to take a photo"}
              </span>
            </div>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        )}

        {error && (
          <p className="text-red-500 text-sm text-center mb-4">{error}</p>
        )}

        {image && (
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="w-full bg-blue-500 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-600 disabled:opacity-40 transition mb-3"
          >
            {uploading ? "Uploading..." : `Upload Page ${pages + 1}`}
          </button>
        )}

        {pages > 0 && !image && (
          <button
            onClick={handleFinish}
            disabled={finishing}
            className="w-full bg-green-500 text-white py-3 rounded-xl font-semibold text-sm hover:bg-green-600 disabled:opacity-40 transition"
          >
            {finishing ? "Processing..." : `Done (${pages} page${pages !== 1 ? "s" : ""})`}
          </button>
        )}
      </div>
    </div>
  );
}
